/**
 * Phase 12 — Target-Pk recommendation surface for the Pk Campaign
 * detail page.
 *
 * Renders four blocks when ``campaign.campaign_type === "target_pk"``:
 *   - Recommendation card: target / achieved / feasibility chip.
 *   - Diff table: variable | from | to | Δ abs | Δ % | unit
 *     (server pre-sorted by |Δ %| desc).
 *   - Plotly convergence chart: candidate Pk per iteration + best-so-far
 *     line + horizontal target reference. (Server-side PNG also available
 *     at /convergence.png — used by the report. Frontend renders the
 *     interactive Plotly version.)
 *   - Narrative paragraph: the auto-generated procurement-friendly
 *     summary from ``target_pk_result.narrative``.
 *
 * Mirrors the SensitivitySection mount pattern.
 */
import { useMemo } from "react";
import Plot from "react-plotly.js";
import { Badge } from "@/components/ui/badge";
import {
    Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";

function fmt(v, d = 3) {
    if (v == null || Number.isNaN(v)) return "—";
    return Number(v).toFixed(d);
}

function fmtPct(v) {
    if (v == null || Number.isNaN(v)) return "—";
    const sign = v >= 0 ? "+" : "";
    return `${sign}${Number(v).toFixed(1)}%`;
}

function RecommendationCard({ rec }) {
    const target = rec.target_pk;
    const achieved = rec.achieved_pk;
    const feasible = rec.feasible;
    return (
        <Card data-testid="pk-target-recommendation-card">
            <CardHeader>
                <CardTitle className="text-base">
                    Target Pk recommendation
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                            Target Pk
                        </div>
                        <div
                            className="text-2xl font-mono"
                            data-testid="pk-target-recommendation-target"
                        >
                            {fmt(target, 2)}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                            Projected achieved Pk
                        </div>
                        <div
                            className="text-2xl font-mono"
                            data-testid="pk-target-recommendation-achieved"
                        >
                            {fmt(achieved, 2)}
                        </div>
                    </div>
                    <div className="self-center">
                        {feasible ? (
                            <Badge
                                className="bg-emerald-500/20 text-emerald-200 border-emerald-500/40"
                                data-testid="pk-target-feasible-chip"
                            >
                                Target achieved
                            </Badge>
                        ) : (
                            <Badge
                                className="bg-amber-500/20 text-amber-200 border-amber-500/40"
                                data-testid="pk-target-infeasible-chip"
                            >
                                Best found — target not feasible
                            </Badge>
                        )}
                    </div>
                </div>
                <div className="mt-3 text-xs text-muted-foreground">
                    {rec.iterations_used} iterations ·{" "}
                    {rec.total_samples_evaluated} simulation runs ·{" "}
                    λ = {fmt(rec.lambda_penalty, 2)}
                </div>
            </CardContent>
        </Card>
    );
}

function DiffTable({ diff }) {
    if (!diff || diff.length === 0) return null;
    return (
        <Card data-testid="pk-target-diff-card">
            <CardHeader>
                <CardTitle className="text-base">
                    Recommended configuration changes
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="overflow-x-auto">
                    <table
                        className="w-full text-xs"
                        data-testid="pk-target-diff-table"
                    >
                        <thead className="text-muted-foreground">
                            <tr>
                                <th className="text-left py-1 pr-3">Variable</th>
                                <th className="text-left py-1 pr-3">Category</th>
                                <th className="text-right py-1 pr-3">From</th>
                                <th className="text-right py-1 pr-3">To</th>
                                <th className="text-right py-1 pr-3">Δ abs</th>
                                <th className="text-right py-1">Δ %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {diff.map((row) => (
                                <tr
                                    key={row.var_id}
                                    className="border-t border-white/5"
                                    data-testid={`pk-target-diff-row-${row.var_id}`}
                                >
                                    <td className="py-1 pr-3 font-mono">
                                        {row.var_id}
                                    </td>
                                    <td className="py-1 pr-3">
                                        <Badge variant="outline" className="text-[10px]">
                                            {row.category}
                                        </Badge>
                                    </td>
                                    <td className="py-1 pr-3 text-right font-mono">
                                        {fmt(row.from, 3)}
                                        {row.unit && row.unit !== "-" && (
                                            <span className="text-muted-foreground ml-1">
                                                {row.unit}
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-1 pr-3 text-right font-mono">
                                        {fmt(row.to, 3)}
                                        {row.unit && row.unit !== "-" && (
                                            <span className="text-muted-foreground ml-1">
                                                {row.unit}
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-1 pr-3 text-right font-mono">
                                        {fmt(row.delta_abs, 3)}
                                    </td>
                                    <td className="py-1 text-right font-mono">
                                        {fmtPct(row.delta_pct)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </CardContent>
        </Card>
    );
}

function ConvergenceChart({ rec }) {
    // Phase 14.8 W5 — wrap `trace` in useMemo so its identity is stable
    // across renders when `rec.convergence_trace` hasn't changed;
    // otherwise the `data` useMemo at line 173 recomputes on every render.
    const trace = useMemo(() => rec.convergence_trace || [], [rec.convergence_trace]);
    const data = useMemo(() => {
        if (trace.length === 0) return [];
        const iters = trace.map((c, i) => c.iteration || i + 1);
        const candidatePk = trace.map((c) => c.candidate_pk ?? 0);
        const bestPk = trace.map((c) => c.best_pk_so_far ?? 0);
        return [
            {
                x: iters,
                y: candidatePk,
                mode: "markers",
                marker: { color: "#94a3b8", size: 8, opacity: 0.7 },
                type: "scatter",
                name: "Candidate Pk",
                hovertemplate: "iter %{x}<br>Pk %{y:.3f}<extra></extra>",
            },
            {
                x: iters,
                y: bestPk,
                mode: "lines+markers",
                line: { color: "#38bdf8", width: 2 },
                marker: { color: "#38bdf8", size: 6 },
                type: "scatter",
                name: "Best-so-far",
                hovertemplate: "iter %{x}<br>best %{y:.3f}<extra></extra>",
            },
        ];
    }, [trace]);

    const layout = useMemo(() => ({
        autosize: true,
        height: 320,
        margin: { l: 56, r: 24, t: 16, b: 48 },
        paper_bgcolor: "rgba(15,23,42,0)",
        plot_bgcolor: "rgba(15,23,42,0)",
        xaxis: { color: "#cbd5e1", gridcolor: "#1e293b", title: "Iteration" },
        yaxis: {
            color: "#cbd5e1", gridcolor: "#1e293b",
            range: [0, 1.05], title: "Pk",
        },
        shapes: [{
            type: "line",
            x0: 0, x1: 1, xref: "paper",
            y0: rec.target_pk, y1: rec.target_pk,
            line: { color: "#facc15", width: 1.4, dash: "dash" },
        }],
        annotations: [{
            x: 1, xref: "paper", y: rec.target_pk,
            xanchor: "right", yanchor: "bottom",
            text: `target ${Number(rec.target_pk).toFixed(2)}`,
            showarrow: false,
            font: { color: "#facc15", size: 10 },
        }],
        legend: {
            orientation: "h",
            y: -0.2,
            font: { color: "#e2e8f0", size: 10 },
        },
    }), [rec.target_pk]);

    if (trace.length === 0) return null;

    return (
        <Card data-testid="pk-target-convergence-card">
            <CardHeader>
                <CardTitle className="text-base">
                    BO convergence trace
                </CardTitle>
            </CardHeader>
            <CardContent>
                <Plot
                    data={data}
                    layout={layout}
                    config={{ displayModeBar: false, responsive: true }}
                    useResizeHandler
                    style={{ width: "100%", height: 320 }}
                    data-testid="pk-target-convergence-plot"
                />
            </CardContent>
        </Card>
    );
}

function NarrativeCard({ rec }) {
    const narrative = rec.narrative;
    if (!narrative) return null;
    return (
        <Card data-testid="pk-target-narrative-card">
            <CardHeader>
                <CardTitle className="text-base">Summary</CardTitle>
            </CardHeader>
            <CardContent>
                <p
                    className="text-sm leading-relaxed text-foreground/90"
                    data-testid="pk-target-narrative-text"
                >
                    {narrative}
                </p>
            </CardContent>
        </Card>
    );
}

export default function TargetPkSection({ campaign }) {
    if (!campaign || campaign.campaign_type !== "target_pk") return null;
    const rec = campaign.target_pk_result;
    if (!rec) {
        const progress = campaign.progress;
        return (
            <Card data-testid="pk-target-pending">
                <CardHeader>
                    <CardTitle className="text-base">
                        Target Pk recommendation
                    </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                    {progress ? (
                        <span data-testid="pk-target-pending-progress">
                            Iteration {progress.iteration} · best Pk so far{" "}
                            {fmt(progress.best_pk_so_far, 2)}
                            {progress.feasible_so_far ? " (target reached)" : ""}.
                        </span>
                    ) : (
                        "Recommendation will populate once the campaign completes."
                    )}
                </CardContent>
            </Card>
        );
    }
    return (
        <div className="space-y-4" data-testid="pk-target-section">
            <RecommendationCard rec={rec} />
            <NarrativeCard rec={rec} />
            <DiffTable diff={rec.diff} />
            <ConvergenceChart rec={rec} />
        </div>
    );
}
